/**
 * The one switch for applying for a OneAmerica Asset Care policy in a state
 * where OneAmerica supports the 3-year AOB option.
 *
 * The application's DocuSign templates (form ICC18 I-31139) have a single box
 * for the AOB period, "2 Years", and the form definition mirrors it with a
 * single option. A 3-year policy sent through them would reach the carrier as
 * 2 years. So while the switch is off, Asset Care applications are unavailable
 * in states where OneAmerica supports the 3-year option, and the form is
 * exactly what it was before 3-year quotes existed.
 *
 * To turn it on: add the 3-year box to every OneAmerica application template
 * (default, CA, FL) as a radio in the group "policyMinBenefitDurationAob" with
 * the value ASSET_CARE_AOB_3_YEARS_OPTION_ID, then set
 * ASSET_CARE_APPLICATION_HAS_3_YEAR_AOB_BOX to true. That adds the option to
 * the form, pre-fills it from the quote, and lets these policies be applied for.
 */
import { FundingPolicyType } from '../appModel';
import { UnitedStatesState } from '../UnitedStatesState';

export const ASSET_CARE_APPLICATION_HAS_3_YEAR_AOB_BOX = false;

/** States where OneAmerica has not introduced the 3-year AOB option. */
export const ASSET_CARE_APPLICATION_UNCHANGED_STATES = [
  'CA',
  'DC',
  'DE',
  'FL',
  'IN',
  'NJ',
  'SC',
] as const;

export const ASSET_CARE_AOB_2_YEARS_OPTION_ID = 'aob';
export const ASSET_CARE_AOB_3_YEARS_OPTION_ID = 'aob-3-years';

const THREE_YEAR_AOB_MONTHS = 36;

function aobMonths(aobDuration: unknown): number | null {
  if (typeof aobDuration !== 'string') return null;
  const amount = parseFloat(aobDuration);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return /month/i.test(aobDuration) ? amount : amount * 12;
}

/** The quote a policy was added from, which is stored as an object or as JSON text. */
function quoteAobDuration(ltcPolicy: PolicyFundingSource): unknown {
  let quote: unknown = ltcPolicy.updatedQuote || ltcPolicy.sourceQuote;
  if (typeof quote === 'string') {
    try {
      quote = JSON.parse(quote);
    } catch {
      return null;
    }
  }
  return quote && typeof quote === 'object' ? (quote as { aobDuration?: unknown }).aobDuration : null;
}

export function isThreeYearAobAssetCarePolicy(ltcPolicy: PolicyFundingSource): boolean {
  if (!isAssetCarePolicy(ltcPolicy)) return false;

  return (
    ltcPolicy.policyAccelerationBenefitDurationMonths === THREE_YEAR_AOB_MONTHS ||
    aobMonths(quoteAobDuration(ltcPolicy)) === THREE_YEAR_AOB_MONTHS
  );
}

export function isAssetCarePolicy(ltcPolicy: PolicyFundingSource): boolean {
  return (
    ltcPolicy.policyType === FundingPolicyType.assetCare ||
    ltcPolicy.policyType === FundingPolicyType.jointAssetCare
  );
}

/** True while a 3-year policy has no way to say "3 years" on the carrier's form. */
export function isWaitingOnThreeYearAobApplication(ltcPolicy: PolicyFundingSource): boolean {
  return !ASSET_CARE_APPLICATION_HAS_3_YEAR_AOB_BOX && isThreeYearAobAssetCarePolicy(ltcPolicy);
}

/**
 * True when the selected application state requires the updated Asset Care form.
 * A missing state remains unclassified so the UI can first reveal its state
 * selection step. A 3-year quote remains unavailable in every state because
 * the current form cannot record that duration, including when its application
 * state is changed to a state where OneAmerica has not introduced 3-year AOB.
 */
export function isAssetCareApplicationUnavailableForState(
  ltcPolicy: PolicyFundingSource,
  stateOfResidenceAbbreviation: string | null | undefined,
): boolean {
  if (
    ASSET_CARE_APPLICATION_HAS_3_YEAR_AOB_BOX ||
    !isAssetCarePolicy(ltcPolicy) ||
    !stateOfResidenceAbbreviation
  ) {
    return false;
  }

  const normalizedState = stateOfResidenceAbbreviation.trim().toUpperCase();
  if (isWaitingOnThreeYearAobApplication(ltcPolicy)) return true;

  return !ASSET_CARE_APPLICATION_UNCHANGED_STATES.some(state => state === normalizedState);
}

export function getAssetCareApplicationUnavailableMessage(
  stateOfResidenceAbbreviation: string,
): string {
  const normalizedState = stateOfResidenceAbbreviation.trim().toUpperCase();
  const stateName =
    UnitedStatesState[normalizedState]?.stateName || stateOfResidenceAbbreviation.trim();

  return `Applications for this policy are temporarily unavailable in ${stateName}. We are working with OneAmerica to update their application forms following the addition of the new 3-year AOB option.`;
}

export function getAssetCareAobOptions(
  hasThreeYearBox = ASSET_CARE_APPLICATION_HAS_3_YEAR_AOB_BOX,
): { id: string; value: string }[] {
  return [
    { id: ASSET_CARE_AOB_2_YEARS_OPTION_ID, value: 'AOB 2 Years' },
    ...(hasThreeYearBox ? [{ id: ASSET_CARE_AOB_3_YEARS_OPTION_ID, value: 'AOB 3 Years' }] : []),
  ];
}

/**
 * The form answer for a quote's AOB period. Everything that is not 3 years keeps
 * the answer the form has always been pre-filled with.
 */
export function getAssetCareAobOptionId(
  aobDuration: string | null | undefined,
  hasThreeYearBox = ASSET_CARE_APPLICATION_HAS_3_YEAR_AOB_BOX,
): string {
  return hasThreeYearBox && aobMonths(aobDuration) === THREE_YEAR_AOB_MONTHS
    ? ASSET_CARE_AOB_3_YEARS_OPTION_ID
    : ASSET_CARE_AOB_2_YEARS_OPTION_ID;
}
